import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

// Use same API URL as the rest of the app
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://139.59.160.11:5020/api';

const AcceptInvitation: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [invitationData, setInvitationData] = useState<any>(null);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Invalid invitation link. No token provided.');
      return;
    }

    // Validate the invitation (don't accept yet)
    const validateInvitation = async () => {
      try {
        
        const response = await fetch(`${API_BASE_URL}/invitations/token/${token}`);

        if (response.ok) {
          const data = await response.json();
          setInvitationData(data.invitation);
          setStatus('success');
          setMessage(`Invitation is valid for ${data.invitation.organization.name}`);
        } else {
          const errorData = await response.json();
          setStatus('error');
          setMessage(errorData.error || 'Invalid or expired invitation');
        }
      } catch (error) {
        console.error('Error validating invitation:', error);
        setStatus('error');
        setMessage('Failed to validate invitation. Please try again.');
      }
    };

    validateInvitation();
  }, [token, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="text-center">
            {status === 'loading' && (
              <div className="flex flex-col items-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                <h2 className="mt-4 text-xl font-semibold text-gray-900">
                  Processing Invitation...
                </h2>
                <p className="mt-2 text-sm text-gray-600">
                  Please wait while we process your invitation.
                </p>
              </div>
            )}

            {status === 'success' && (
              <div className="flex flex-col items-center">
                <CheckCircleIcon className="h-12 w-12 text-green-600" />
                <h2 className="mt-4 text-xl font-semibold text-gray-900">
                  Invitation Valid!
                </h2>
                <p className="mt-2 text-sm text-gray-600">
                  You've been invited to join {invitationData?.organization?.name}
                </p>
                {invitationData && (
                  <div className="mt-4 p-4 bg-green-50 rounded-lg">
                    <p className="text-sm text-green-800">
                      <strong>Organization:</strong> {invitationData.organization?.name}
                    </p>
                    <p className="text-sm text-green-800">
                      <strong>Role:</strong> {invitationData.role}
                    </p>
                    <p className="text-sm text-green-800">
                      <strong>Email:</strong> {invitationData.email}
                    </p>
                  </div>
                )}
                <div className="mt-6">
                  <button
                    onClick={() => navigate(`/register?invitation=${token}`)}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Create Your Account
                  </button>
                </div>
                <p className="mt-4 text-xs text-gray-500">
                  Already have an account? 
                  <button
                    onClick={() => navigate('/login')}
                    className="text-blue-600 hover:text-blue-500 ml-1"
                  >
                    Sign in here
                  </button>
                </p>
              </div>
            )}

            {status === 'error' && (
              <div className="flex flex-col items-center">
                <XCircleIcon className="h-12 w-12 text-red-600" />
                <h2 className="mt-4 text-xl font-semibold text-gray-900">
                  Invitation Error
                </h2>
                <p className="mt-2 text-sm text-gray-600">
                  {message}
                </p>
                <div className="mt-6">
                  <button
                    onClick={() => navigate('/login')}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Go to Login
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitation;
